import { useEffect, useMemo, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import {
  useProjectMfaSettings,
  useUpdateProjectMfaSettings,
} from '../../hooks/useProjectMfaSettings';
import { Button, Field, FormSection, Input } from '../../ui';
import type {
  ProjectMfaMethod,
  ProjectMfaPolicy,
  ProjectMfaSettingsUpdate,
} from '../../types/projectMfaSettings';
import './ProjectMfaSettings.css';

interface ProjectMfaSettingsProps {
  projectId: string;
  canManage: boolean;
}

const POLICY_OPTIONS: { value: ProjectMfaPolicy; label: string; hint: string }[] = [
  { value: 'disabled', label: 'Disabled', hint: 'End users cannot enroll in MFA' },
  {
    value: 'optional',
    label: 'Optional',
    hint: 'End users may enroll in MFA but it is not enforced',
  },
  {
    value: 'required',
    label: 'Required',
    hint: 'End users must enroll in MFA to access the project',
  },
];

const METHOD_OPTIONS: { value: ProjectMfaMethod; label: string; description: string }[] = [
  {
    value: 'totp',
    label: 'Authenticator app (TOTP)',
    description: 'Time-based codes from apps like 1Password, Authy, Google Authenticator',
  },
  {
    value: 'email',
    label: 'Email code',
    description: 'One-time codes delivered to the end user’s email address',
  },
  {
    value: 'sms',
    label: 'SMS code',
    description: 'One-time codes sent to the end user’s phone (carrier charges may apply)',
  },
];

export function ProjectMfaSettings({ projectId, canManage }: ProjectMfaSettingsProps) {
  const { data: settings, isLoading, isError, error } = useProjectMfaSettings(projectId);
  const updateSettings = useUpdateProjectMfaSettings();

  const [policy, setPolicy] = useState<ProjectMfaPolicy>('disabled');
  const [methods, setMethods] = useState<ProjectMfaMethod[]>(['totp']);
  const [issuerName, setIssuerName] = useState('');
  const [saved, setSaved] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!settings) return;
    setPolicy(settings.policy);
    setMethods(settings.methods_allowed);
    setIssuerName(settings.issuer_name ?? '');
  }, [settings]);

  const isDirty = useMemo(() => {
    if (!settings) return false;
    const sortedNew = [...methods].sort().join(',');
    const sortedCurrent = [...settings.methods_allowed].sort().join(',');
    return (
      policy !== settings.policy ||
      sortedNew !== sortedCurrent ||
      (issuerName || null) !== settings.issuer_name
    );
  }, [settings, policy, methods, issuerName]);

  const willRequireExisting = settings && policy === 'required' && settings.policy !== 'required';

  const toggleMethod = (method: ProjectMfaMethod) => {
    setMethods(prev =>
      prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
    );
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    if (!canManage || !settings) return;

    if (policy !== 'disabled' && methods.length === 0) {
      setValidationError('Select at least one MFA method when policy is not disabled.');
      return;
    }

    const payload: ProjectMfaSettingsUpdate = {
      policy,
      methods_allowed: methods,
      issuer_name: issuerName.trim() || null,
    };

    try {
      await updateSettings.mutateAsync({ projectId, data: payload });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      /* surfaced via mutation error below */
    }
  };

  if (isLoading) {
    return <p className="project-mfa-settings__loading">Loading MFA settings…</p>;
  }
  if (isError || !settings) {
    return (
      <p className="project-mfa-settings__error">
        {error?.message || 'Failed to load MFA settings.'}
      </p>
    );
  }

  return (
    <FormSection
      title={
        <span className="project-mfa-settings__title">
          <ShieldCheck size={18} aria-hidden="true" /> End-User Multi-Factor Authentication
        </span>
      }
      description="Choose whether end users can or must enroll in MFA, which methods they may use, and the issuer name displayed in their authenticator apps."
    >
      <form onSubmit={handleSave} className="project-mfa-settings__form">
        <Field
          label="MFA Policy"
          htmlFor="mfaPolicy"
          hint="Optional makes MFA available to end users; Required forces every end user to enroll before they can sign in."
        >
          <select
            id="mfaPolicy"
            className="project-mfa-settings__select"
            value={policy}
            disabled={!canManage}
            onChange={e => setPolicy(e.target.value as ProjectMfaPolicy)}
          >
            {POLICY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label} — {opt.hint}
              </option>
            ))}
          </select>
        </Field>

        <fieldset className="project-mfa-settings__methods" disabled={!canManage}>
          <legend className="project-mfa-settings__methods-legend">Allowed methods</legend>
          {METHOD_OPTIONS.map(opt => {
            const checked = methods.includes(opt.value);
            return (
              <label key={opt.value} className="project-mfa-settings__method">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!canManage}
                  onChange={() => toggleMethod(opt.value)}
                />
                <span>
                  <span className="project-mfa-settings__method-label">{opt.label}</span>
                  <span className="project-mfa-settings__method-description">
                    {opt.description}
                  </span>
                </span>
              </label>
            );
          })}
        </fieldset>

        <Field
          label="Issuer name (TOTP)"
          htmlFor="mfaIssuer"
          hint="Shown next to the account name inside authenticator apps. Leave blank to use the project name."
        >
          <Input
            id="mfaIssuer"
            type="text"
            value={issuerName}
            placeholder={settings.resolved_issuer_name}
            disabled={!canManage}
            onChange={e => setIssuerName(e.target.value)}
          />
        </Field>

        {willRequireExisting && (
          <p className="project-mfa-settings__warning" role="alert">
            Switching to <strong>Required</strong> will force existing end users without MFA to
            enroll on their next sign-in.
          </p>
        )}

        {validationError && <p className="project-mfa-settings__error">{validationError}</p>}
        {updateSettings.isError && (
          <p className="project-mfa-settings__error">
            {updateSettings.error?.message || 'Failed to save MFA settings'}
          </p>
        )}
        {saved && <p className="project-mfa-settings__success">MFA settings saved.</p>}

        {canManage && (
          <div className="project-mfa-settings__actions">
            <Button
              type="submit"
              loading={updateSettings.isPending}
              disabled={!isDirty || updateSettings.isPending}
            >
              {updateSettings.isPending ? 'Saving…' : 'Save MFA Settings'}
            </Button>
          </div>
        )}
      </form>
    </FormSection>
  );
}
